<template>
  <div v-if="contextData">
    <!-- A 樣式：完整資料 -->
    <SectionListText02
      v-if="contentType === 'full-content'"
      :title="contextData.title"
      :description="contextData.description"
      :items="contextData?.items"
      :thumbnail="contextThumbnail"
      :wistia-code="contextData.wistiaCode"
      :button01="button01"
      :button02="button02"
    />
    <!-- B 樣式：不填列點資料 -->
    <SectionSplit06
      v-else-if="contentType === 'non-items'"
      :title="contextData.title"
      :description="contextData.description"
      :thumbnail="contextThumbnail"
      :wistia-code="contextData.wistiaCode"
      :button01="button01"
      :button02="button02"
    />
    <!-- C 樣式：不填列點、影片/圖資料 -->
    <SectionSplit04
      v-else
      :title="contextData.title"
      :description="contextData.description"
      :button01="button01"
      :button02="button02"
    />
  </div>
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IItem {
    name: string;
    description: string;
  }

  interface IButton {
    text: string;
    link: string;
    trackingCode?: string;
  }

  interface IOverviewData {
    title: string;
    description: string;
    buttons: IButton[];
    items?: IItem[];
    thumbnail?: string;
    wistiaCode?: string;
  }

  interface IOverview {
    component: 'overview';
    meta: {
      navigable?: string;
    };
    data: IOverviewData;
  }

  /**
   * A樣式：完整資料（full-content）
   * B樣式：不填列點資料（non-items）
   * C樣式：不填列點、影片/圖資料（only-content）
   */

  type TOverviewContent = 'full-content' | 'non-items' | 'only-content';

  type IProps = ISuiteProps<IOverview>;

  const props = defineProps<IProps>();

  const { provideFormatUrl } = useFormatUrl();
  const { provideSuiteThumbnailUrl } = useImagePath(props.urlConnector);

  const contextData = computed((): IOverviewData => props.suiteData.data);

  const contextThumbnail = computed((): string =>
    provideSuiteThumbnailUrl(contextData.value.thumbnail || '')
  );

  const button01 = computed((): IButton | undefined => {
    const button = contextData.value.buttons[0];
    return button
      ? { ...button, link: provideFormatUrl(button.link) }
      : undefined;
  });

  const button02 = computed((): IButton | undefined => {
    const button = contextData.value.buttons[1];
    return button
      ? { ...button, link: provideFormatUrl(button.link) }
      : undefined;
  });

  const contentType = computed((): TOverviewContent => {
    const { items, thumbnail } = contextData.value;
    const hasItems = items && items.length > 0;
    const hasMedia = !!thumbnail;

    if (hasItems && hasMedia) {
      return 'full-content';
    } else if (!hasItems && hasMedia) {
      return 'non-items';
    }
    return 'only-content';
  });

  export type { IButton, IItem, IOverview, IOverviewData };
</script>
